import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useRegistration } from "../context/RegistrationContext";
import { useRegistrationWizard } from "../hooks/useRegistrationWizard";
import Navbar from "../components/Navbar";
import OrderSummary from "../components/OrderSummary";
import ServicesEditor from "../components/ServicesEditor";
import { ParticipantCard } from "../components/register/ParticipantCard";
import { GuardianStep } from "../components/register/GuardianStep";
import { WizardProgress, WizardNav, ReviewStep } from "../components/register/WizardParts";
import { cardCls } from "../components/register/FormBits";
import { Plus, Sparkles } from "lucide-react";

const STEPS = ["Participants & Kit", "Optional Services", "Guardian & Consent", "Review"];

function ClosedNotice() {
  return (
    <div><Navbar />
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold text-[#03281B]">Registration Closed</h1>
        <p className="text-slate-600 mt-3">Registration for Sporting CP Camp Arkansas 2027 is now closed.</p>
      </div>
    </div>
  );
}

function ParticipantsStep({ participants, sizes, update, remove, add }) {
  return (
    <div data-testid="step-1-participant-form" className="space-y-6">
      {participants.map((p, i) => (
        <ParticipantCard key={p._k} p={p} i={i} sizes={sizes} canRemove={participants.length > 1} onChange={(np) => update(i, np)} onRemove={() => remove(i)} />
      ))}
      <button data-testid="add-participant-btn" onClick={add} className="w-full rounded-2xl border-2 border-dashed border-[#008057]/40 text-[#008057] font-bold py-4 flex items-center justify-center gap-2 hover:bg-[#F2F8F5] transition-colors">
        <Plus size={20} /> Add Another Participant
      </button>
    </div>
  );
}

function ServicesStep({ participants, update }) {
  return (
    <div data-testid="step-3-optional-services" className="space-y-6">
      {participants.map((p, i) => (
        <div key={p._k} className={cardCls}>
          <div className="flex items-center gap-2 font-display font-bold text-lg text-[#03281B] mb-5"><Sparkles size={20} className="text-[#F3C242]" /> {p.first_name || `Participant ${i + 1}`} &mdash; Optional Services</div>
          <ServicesEditor participant={p} onChange={(np) => update(i, np)} />
        </div>
      ))}
    </div>
  );
}

export default function Register() {
  const navigate = useNavigate();
  const reg = useRegistration();
  const { config, participants, guardian, setGuardian, giftRecipientIndex, setGiftRecipientIndex, quote } = reg;
  const [searchParams] = useSearchParams();
  const { step, update, add, remove, next, back } = useRegistrationWizard(reg, STEPS.length, searchParams.get("ref"));

  if (config?.registration_closed) return <ClosedNotice />;

  const sizes = config?.clothing_sizes || { tshirt: [], shorts: [], socks: [] };
  const proceedToCheckout = () => {
    if (!quote || !quote.total) { toast.error("Unable to calculate total. Please review your selections."); return; }
    navigate("/checkout");
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <WizardProgress steps={STEPS} step={step} />
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {step === 0 && <ParticipantsStep participants={participants} sizes={sizes} update={update} remove={remove} add={add} />}
            {step === 1 && <ServicesStep participants={participants} update={update} />}
            {step === 2 && (
              <GuardianStep guardian={guardian} setGuardian={setGuardian} consents={reg.consents} setConsents={reg.setConsents}
                participants={participants} showGift={config?.gifts?.remaining > 0 && participants.length > 1}
                giftRecipientIndex={giftRecipientIndex} setGiftRecipientIndex={setGiftRecipientIndex} />
            )}
            {step === 3 && <ReviewStep quote={quote} participants={participants} guardian={guardian} />}
            <WizardNav step={step} last={STEPS.length - 1} onBack={back} onNext={next} onCheckout={proceedToCheckout} />
          </div>
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-20"><OrderSummary /></div>
          </div>
        </div>
      </div>
    </div>
  );
}
